<script setup lang="ts">
import { computed, ref } from 'vue'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import PremiumActionModal from '@/components/PremiumActionModal.vue'
import type { ActionPermissions } from '@/components/limit_action_copy'
import { useContentAccessStore } from '@/content_access/content_access.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { CONTENT_TYPES } from '@/util/access_visibility'
import ItemModal from './ItemModal.vue'
import ObjectiveHeading from './ObjectiveHeading.vue'
import PrintableObjective from './PrintableObjective.vue'
import type { ContentAccessInfo } from './content_access_info'
import { OBJECTIVE_EVENT_NAMES } from './event_names'
import { levelBorderClass, levelSolidButtonClass, type ObjectiveLevel } from './objective_levels'
import type { PrintedObjectiveOptions } from './objective_print'

const props = withDefaults(
  defineProps<{
    level: ObjectiveLevel
    title?: string
    text?: string
    items?: string[]
    pageUuid?: string
    accessInfo: ContentAccessInfo
    printPermissions: ActionPermissions
  }>(),
  { title: 'Measurable Objective', text: '', items: () => [], pageUuid: '' }
)

const analytics = getAnalyticsTracker().getChildContext({
  obj_level: computed(() => props.level),
  access: computed(() => !props.printPermissions.block)
})
const contentAccessStore = useContentAccessStore()

const isItemModalOpen = ref(false)
const isPrintViewOpen = ref(false)
const isPrintBlockedModalOpen = ref(false)

const itemId = computed(() => props.items[0] ?? '')
const hasItem = computed(() => Boolean(itemId.value))

function openItemModal() {
  isItemModalOpen.value = true
  analytics.sendEvent(OBJECTIVE_EVENT_NAMES.CLICKED_OPEN_ASSESSMENT, {}, { cu: props.pageUuid, ct: props.title })
}

function closeItemModal() {
  isItemModalOpen.value = false
}

function openPrintView() {
  analytics.sendEvent(OBJECTIVE_EVENT_NAMES.CLICKED_PRINT_OBJECTIVE)
  if (props.printPermissions.block) {
    isPrintBlockedModalOpen.value = true
    return
  }
  isPrintViewOpen.value = true
}

function closePrintView() {
  isPrintViewOpen.value = false
}

function trackPrint(printOptions: PrintedObjectiveOptions) {
  contentAccessStore.trackPrint({
    contentType: CONTENT_TYPES.SUPER_PAGE_ASSESSMENT,
    contentTitle: props.title,
    contentUuid: itemId.value,
    otherData: {
      superpage_uuid: props.pageUuid,
      obj_level: props.level,
      print_options: printOptions
    }
  })
}
</script>

<template>
  <div class="mb-7 rounded-b-xs border bg-white shadow-sm" :class="levelBorderClass(level)" data-testid="objective">
    <CopyTrackWrapper type="objective" :level="level" gate-copy>
      <ObjectiveHeading :level="level" :title="title" :is-quarter-shown="false" />
      <div class="p-4">
        <div class="text-text-xl font-regular leading-6">
          <slot name="body" />
        </div>
        <template v-if="hasItem">
          <button
            type="button"
            class="mt-5 flex items-center gap-2 rounded-xs px-3.5 py-2 text-left tracking-wide focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
            :class="levelSolidButtonClass(level)"
            data-testid="objective-view-task"
            @click="openItemModal"
          >
            <span class="material-icons-round text-display-sm text-white" aria-hidden="true">insert_drive_file</span>
            <span>
              <span class="block font-medium uppercase text-white">View Student Task</span>
              <span class="block text-gray-300">Downloadable resources included</span>
            </span>
          </button>
          <ItemModal
            :item-id="itemId"
            :level="level"
            :title="title"
            :is-open="isItemModalOpen"
            :access-info="accessInfo"
            @update:is-open="closeItemModal"
            @print="openPrintView"
          >
            <template #objective>
              <slot name="objective" />
            </template>
          </ItemModal>
          <PrintableObjective
            v-if="isPrintViewOpen"
            :item-id="itemId"
            :objective-level="level"
            :objective-title="title"
            :objective-text="text"
            @close="closePrintView"
            @print="trackPrint"
          />
          <PremiumActionModal v-model:external-open="isPrintBlockedModalOpen" :action-permissions="printPermissions" />
        </template>
        <div>
          <slot name="footer" />
        </div>
      </div>
    </CopyTrackWrapper>
  </div>
</template>
